import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscribeButton = () => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);

  useEffect(() => {
    if (open && Object.keys(events).length === 0) {
      axios.get(`${API}/notifications/events`).then(r => {
        const ev = r.data?.events || {};
        setEvents(ev);
        setSelected(Object.keys(ev));
      }).catch(() => {});
    }
  }, [open, events]);

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email obrigatório'); return; }
    if (selected.length === 0) { setError('Escolhe pelo menos um tipo de aviso'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/notifications/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        events: selected,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao subscrever');
    }
    setSubmitting(false);
  };

  const handleUnsubscribe = async () => {
    if (!email.trim()) { setError('Email obrigatório'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/notifications/unsubscribe`, { email: email.trim(), events: ['new_match'] });
      setDone(true);
    } catch {
      setError('Erro ao remover');
    }
    setSubmitting(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setError('');
      setShowUnsubscribe(false);
    }, 200);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 bg-green-500 hover:bg-green-400 text-zinc-950 px-3 py-1.5 rounded-lg font-bold text-xs sm:text-sm transition-colors"
        data-testid="open-subscribe-btn"
        title="Receber notificações por email"
      >
        <Bell size={16} strokeWidth={2.5} />
        <span className="hidden sm:inline">ALERTAS</span>
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={close}>
          <div
            className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="subscribe-modal"
          >
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="text-xl font-black tracking-tight flex items-center gap-2">
                <Bell size={22} /> {showUnsubscribe ? 'CANCELAR ALERTAS' : 'RECEBER ALERTAS'}
              </h2>
              <button onClick={close} className="text-zinc-500 hover:text-zinc-900" data-testid="close-subscribe-btn">
                <X size={24} />
              </button>
            </div>

            {done ? (
              <div className="p-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check size={32} className="text-green-600" strokeWidth={3} />
                </div>
                <p className="text-lg font-bold mb-2">
                  {showUnsubscribe ? 'Inscrição removida!' : 'Inscrição confirmada!'}
                </p>
                <p className="text-sm text-zinc-500 mb-4">
                  {showUnsubscribe ? 'Não vais receber mais avisos.' : `Vamos avisar-te por email em ${email}.`}
                </p>
                <button onClick={close} className="bg-zinc-900 text-white font-bold px-6 py-2 rounded-lg" data-testid="subscribe-done-btn">
                  Fechar
                </button>
              </div>
            ) : showUnsubscribe ? (
              <form onSubmit={(e) => { e.preventDefault(); handleUnsubscribe(); }} className="p-5 space-y-4">
                <p className="text-sm text-zinc-600">Insere o email para deixar de receber avisos.</p>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="o-teu-email@exemplo.com"
                  className="w-full border-2 border-zinc-300 rounded-lg px-3 py-2 font-medium"
                  data-testid="unsubscribe-email-input"
                />
                {error && <p className="text-red-600 text-sm font-bold">{error}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowUnsubscribe(false)}
                    className="flex-1 border-2 border-zinc-300 font-bold py-2 rounded-lg hover:bg-zinc-100"
                  >
                    Voltar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-lg disabled:opacity-50"
                    data-testid="confirm-unsubscribe-btn"
                  >
                    {submitting ? 'A remover…' : 'Remover'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <p className="text-sm text-zinc-600">
                  Recebe um email sempre que houver uma novidade no Domingão.
                </p>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-1 text-zinc-700">Nome (opcional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="O teu nome"
                    className="w-full border-2 border-zinc-300 rounded-lg px-3 py-2 font-medium"
                    data-testid="subscribe-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-1 text-zinc-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="o-teu-email@exemplo.com"
                    className="w-full border-2 border-zinc-300 rounded-lg px-3 py-2 font-medium"
                    data-testid="subscribe-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2 text-zinc-700">Quero ser avisado sobre:</label>
                  <div className="space-y-2">
                    {Object.entries(events).map(([key, label]) => (
                      <label
                        key={key}
                        className={`flex items-center gap-2 border-2 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                          selected.includes(key) ? 'border-green-500 bg-green-50' : 'border-zinc-200 hover:border-zinc-400'
                        }`}
                        data-testid={`subscribe-event-${key}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(key)}
                          onChange={() => toggle(key)}
                          className="w-4 h-4 accent-green-500"
                        />
                        <span className="text-sm font-bold">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {error && <p className="text-red-600 text-sm font-bold">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-green-500 hover:bg-green-400 text-zinc-950 font-black py-3 rounded-lg disabled:opacity-50"
                  data-testid="confirm-subscribe-btn"
                >
                  {submitting ? 'A SUBSCREVER…' : 'SUBSCREVER'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowUnsubscribe(true); setError(''); }}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-900 underline"
                  data-testid="show-unsubscribe-btn"
                >
                  Já estou inscrito → quero remover
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SubscribeButton;
